import { Injectable } from '@angular/core';
import { USER_DATA } from '@tacliatech/types';
import { environment } from '@web-frontend/environments';
import { getCountryCodeByCompanyFromLocalStorage } from '@web-frontend/shared/utils/country-validator';
import * as configcat from 'configcat-js';
import { FEATURE_FLAGS } from '@tacliatech/types';

type FeatureFlagValue = boolean | string | number;
type FeatureFlagArray = Array<{ [key: string]: FeatureFlagValue }>;

// For microfrontend support
function exposeFeatureFlagCapability(client: {
  getValueAsync: (
    key: string,
    defaultValue: FeatureFlagValue,
    customAttributes?: {
      [key: string]: configcat.UserAttributeValue;
    },
    customParser?: (value: FeatureFlagValue) => FeatureFlagValue
  ) => Promise<FeatureFlagValue>;
}) {
  if (window && !(window as any).FeatureFlagClient) {
    (window as any).FeatureFlagClient = client;
  }
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private configCatClient: configcat.IConfigCatClient;

  constructor() {
    this.initConfigCatClient();
  }

  private initConfigCatClient(): void {
    try {
      const logger = configcat.createConsoleLogger(configcat.LogLevel.Info);
      const setupConfigCat: configcat.IJSAutoPollOptions = {
        logger: logger,
        pollIntervalSeconds: 1800, // 30 mins
      };
      if (environment.production) {
        delete setupConfigCat.logger;
      }
      this.configCatClient = configcat.getClient(
        environment.configCatKey,
        configcat.PollingMode.LazyLoad,
        setupConfigCat
      );
      exposeFeatureFlagCapability({
        getValueAsync: this.getValue.bind(this),
      });
    } catch (error) {
      console.error('Error initializing ConfigCat client', error);
    }
  }

  private safeParse<T>(value: string | null, fallback: T): T {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch {
      return fallback;
    }
  }

  private appendFeatureFlagToLocalStorage(
    key: string,
    value: FeatureFlagValue
  ) {
    const featureFlags = this.safeParse<FeatureFlagArray>(
      localStorage.getItem(FEATURE_FLAGS),
      []
    ).filter((f) => !Object.prototype.hasOwnProperty.call(f, key));

    featureFlags.push({ [key]: value });
    localStorage.setItem(FEATURE_FLAGS, JSON.stringify(featureFlags));
  }

  public async getValue(
    key: string,
    defaultValue: FeatureFlagValue,
    customAttributes?: {
      [key: string]: configcat.UserAttributeValue;
    },
    customParser?: (value: FeatureFlagValue) => FeatureFlagValue
  ): Promise<FeatureFlagValue> {
    if (!this.configCatClient) return Promise.resolve(defaultValue);
    return this.configCatClient
      .getValueAsync(key, defaultValue, this.getUserObject(customAttributes))
      .then((value) => {
        const parsed = customParser ? customParser(value) : value;
        this.appendFeatureFlagToLocalStorage(key, parsed);
        return parsed;
      });
  }

  private getUserObject(customAttributes?: {
    [key: string]: configcat.UserAttributeValue;
  }) {
    const userDataString = localStorage.getItem(USER_DATA);
    const userData = JSON.parse(userDataString);
    if (!userData?.id || !userData?.email)
      return this.getNonUserObject(customAttributes);

    const { id, email } = userData;
    const country = getCountryCodeByCompanyFromLocalStorage();
    const user = new configcat.User(id, email, country, customAttributes);

    return user;
  }

  private getNonUserObject(customAttributes?: {
    [key: string]: configcat.UserAttributeValue;
  }) {
    const identifier = customAttributes.identifier as string;
    if (!identifier) return null;

    const user = new configcat.User(identifier);
    return user;
  }
}
